<template>
  <log-quick-submit
    v-if="isEditing"
    v-model="comment"
    :placeholder="$t('ACTIVITY.COMMENTS.COMMENT_PLACEHOLDER')"
    :loading="isPending"
    data-cy="log-comment-edit"
    @submit="updateComment"
  >
    <template #actionBar>
      <avatar-with-name :user="data.user" data-cy="log-comment-author" />
    </template>

    <template #buttons>
      <modal-buttons
        class="max-h-8 bg-white px-[0]"
        style="background-color: transparent !important"
        :disabled="!comment.trim() || isPending"
        :on-cancel="cancelEditing"
        :on-save="updateComment"
      />
    </template>
  </log-quick-submit>

  <div v-else class="space-y-2" data-cy="log-comment">
    <div class="flex w-full justify-between space-x-1-5">
      <p
        class="overflow-hidden break-words text-gray-500"
        data-cy="log-comment-content"
      >
        {{ isPending ? pendingComment : comment }}
      </p>
      <action-menu-wrapper
        :items="menuItems"
        :offset="offset"
        teleport
        close-menu-on-trigger-hide
      />
    </div>
    <div class="flex w-full items-center space-x-1-5 text-xs font-medium">
      <avatar-with-name :user="data.user" data-cy="log-comment-author" />
      <span>•</span>
      <span>
        {{ relativeDate }}
      </span>
    </div>
  </div>

  <confirm-modal
    v-if="showDeleteModal"
    :title="`${$t('COMMON.DELETE')} ${$t('ACTIVITY.COMMENTS.TITLE')}`"
    :description="
      $t('COMMON.DELETE_CONFIRMATION', {
        source: $t('ACTIVITY.COMMENTS.TITLE').toLowerCase(),
      })
    "
    :confirm-button="$t('COMMON.DELETE')"
    confirm-button-type="error"
    :close="() => (showDeleteModal = false)"
    @confirm="deleteComment"
  />
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";

import { COMMON_MENU_OFFSET } from "@/helpers/constants";
import { formatTimeAgo } from "@/helpers/formatting";
import { CommunicationLogContextKey } from "@/helpers/symbols";
import {
  useDeleteCommentMutation,
  useUpdateCommentMutation,
} from "@/hooks/queries/useInbox";

import LogQuickSubmit from "@/components/communicationLogs/forms/LogQuickSubmit.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import ActionMenuWrapper, {
  type MenuItem,
} from "@/components/ui/ActionMenuWrapperV2.vue";
import AvatarWithName from "@/components/ui/AvatarWithName.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";

import type { CommunicationLogComment } from "@/models/communicationLogs";

const { data, smsId } = defineProps<{
  data: CommunicationLogComment;
  smsId?: number | null;
}>();
const offset = COMMON_MENU_OFFSET;

const context = inject(CommunicationLogContextKey, {
  type: null,
  logId: null,
});

const { t } = useI18n();
const showDeleteModal = ref(false);

const isEditing = ref(false);
const comment = ref(data.comment);

const {
  mutateAsync: updateCommentMutation,
  isPending,
  variables,
} = useUpdateCommentMutation(smsId);

const { mutateAsync: deleteCommentMutation } = useDeleteCommentMutation(smsId);

const pendingComment = computed(() => variables.value?.comment);

const menuItems = computed(() => {
  const items: MenuItem[] = [
    {
      label: t("COMMON.EDIT"),
      handler: () => (isEditing.value = true),
    },
    {
      label: t("COMMON.DELETE"),
      handler: () => (showDeleteModal.value = true),
    },
  ];

  return items;
});

const relativeDate = computed(() => {
  return formatTimeAgo(data.created_at);
});

const cancelEditing = () => {
  comment.value = data.comment;
  isEditing.value = false;
};

const deleteComment = async () => {
  const { logId, type } = context;
  if (logId === null || type === null) {
    return;
  }
  await deleteCommentMutation({
    commentId: data.id,
    logId,
    type,
  });
  showDeleteModal.value = false;
};

const updateComment = async () => {
  const { logId, type } = context;
  if (logId === null || type === null) {
    return;
  }
  await updateCommentMutation({
    type,
    logId,
    commentId: data.id,
    comment: comment.value,
  });
  isEditing.value = false;
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

:deep(.dropdown) {
  @apply -translate-x-full -translate-y-5 transform;
}
</style>
